import { useEffect, useState, useCallback } from "react";
import { FolderKey, ShieldCheck, AlertTriangle, ExternalLink, Trash2 } from "lucide-react";
import { fsApiSupported, getRootHandle, pickRootHandle, clearRootHandle, ensurePermission } from "@/lib/localVault";
import { toast } from "sonner";

/**
 * Componente compacto — mostra o estado atual do "cofre local" e permite
 * escolher/limpar a pasta raiz. Usado em `/perfil` e no topo dos módulos de
 * Cálculos para garantir que o advogado configure isso cedo.
 */
export function LocalVaultCard({ compact = false }) {
  const [supported] = useState(fsApiSupported());
  const [handle, setHandle] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    setChecking(true);
    try {
      const h = await getRootHandle();
      setHandle(h);
    } finally { setChecking(false); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const pick = async () => {
    try {
      await pickRootHandle();
      toast.success("Pasta local configurada com sucesso.");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Não foi possível configurar a pasta.");
    }
  };
  const forget = async () => {
    if (!window.confirm("Esquecer a pasta local? Os arquivos permanecem no seu computador, apenas o Hermes deixa de gravar nela automaticamente.")) return;
    await clearRootHandle();
    toast.success("Pasta esquecida.");
    await refresh();
  };
  const reauth = async () => {
    const h = await getRootHandle();
    if (!h) return pick();
    const ok = await ensurePermission(h, "readwrite");
    if (ok) toast.success("Permissão de escrita concedida.");
    else toast.error("Permissão negada.");
    await refresh();
  };

  if (!supported) {
    return (
      <div className={`rounded-2xl border border-amber-200 bg-amber-50 p-4 ${compact ? "" : ""}`}
           data-testid="vault-unsupported">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5"/>
          <div className="text-sm text-amber-900 leading-relaxed">
            <b>Seu navegador não suporta a gravação direta em pastas.</b> Chrome,
            Edge e Opera modernos suportam. No Firefox e Safari, o Hermes cai
            para o modo de download tradicional (você escolhe o local a cada
            arquivo). Recomendamos abrir o Hermes no Chrome ou Edge.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border ${handle ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"} p-4`}
         data-testid="vault-card">
      <div className="flex items-start gap-3">
        {handle
          ? <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5"/>
          : <FolderKey  className="w-5 h-5 text-slate-500 shrink-0 mt-0.5"/>}
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-900">
            {handle ? "Cofre local do Hermes configurado" : "Configurar cofre local"}
          </div>
          <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">
            {handle
              ? "O Hermes grava todos os documentos e cálculos automaticamente em subpastas por cliente dentro da pasta que você escolheu. Nada mais precisa ser feito."
              : "Escolha uma pasta no seu computador (ex.: Documentos / Hermes). O sistema criará automaticamente subpastas por cliente e gravará ali todos os originais e cálculos gerados."}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {!handle && (
              <button onClick={pick} disabled={checking}
                data-testid="vault-pick"
                className="h-9 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium inline-flex items-center gap-1.5">
                <FolderKey className="w-3.5 h-3.5"/> Escolher pasta
              </button>
            )}
            {handle && (
              <>
                <button onClick={reauth} data-testid="vault-reauth"
                  className="h-9 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-medium hover:bg-slate-100">
                  Reautorizar acesso
                </button>
                <button onClick={pick} data-testid="vault-change"
                  className="h-9 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-medium hover:bg-slate-100">
                  Trocar pasta
                </button>
                <button onClick={forget} data-testid="vault-forget"
                  className="h-9 px-3 rounded-lg bg-white border border-rose-200 text-rose-700 text-xs font-medium hover:bg-rose-50 inline-flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5"/> Esquecer
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Banner permanente exibido no topo dos módulos de cálculo. Deixa MUITO claro
 * a política de retenção de 5 dias e a preferência pelo cofre local.
 */
export function RetentionBanner({ retention }) {
  const days = retention?.purge_after_days || 5;
  return (
    <div className="rounded-xl border-l-4 border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900"
         data-testid="retention-banner">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
        <div className="leading-relaxed">
          <b>Salvamento local obrigatório.</b> Todos os documentos e cálculos são
          gravados <b>fisicamente no seu computador</b>, em <code className="px-1 py-0.5 rounded bg-white/70">Hermes/[Nome do Cliente]/…</code>.
          O servidor guarda os originais por apenas <b>{days} dias</b> — o tempo
          necessário para OCR, classificação e cálculo — e depois apaga
          automaticamente. Certifique-se de que o cofre local está
          configurado no seu <a href="/profile" className="underline font-semibold">perfil</a>.
        </div>
      </div>
    </div>
  );
}
